import React, { useEffect, useState } from "react";
import "./AddCompanyModal.css";
import { EntityTypes } from "../../Models/Store";

type Props = {
  onClose: () => void;
  onSave: (data: {
    name: string;
    codes: string[];
    selectedEntityTypes: EntityTypes[];
  }) => Promise<void> | void;
  mode?: "create" | "edit";
  initialValues?: Partial<{
    name: string;
    codes: string[];
    selectedEntityTypes: EntityTypes[];
  }>;
  titleOverride?: string;
  submitLabelOverride?: string;
};

const ENTITY_OPTIONS: EntityTypes[] = [
  EntityTypes.EPSE,
  EntityTypes.SIDJ,
  EntityTypes.JVB,
  EntityTypes.JCE,
];

const AddCompanyModal: React.FC<Props> = ({
  onClose,
  onSave,
  mode = "create",
  initialValues,
  titleOverride,
  submitLabelOverride,
}) => {
  const [name, setName] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const [codeInput, setCodeInput] = useState("");
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<EntityTypes[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(initialValues?.name ?? "");
    setCodes(Array.isArray(initialValues?.codes) ? initialValues!.codes!.filter(Boolean) : []);
    setSelectedEntityTypes(initialValues?.selectedEntityTypes ?? ENTITY_OPTIONS);
  }, [initialValues]);

  const title = titleOverride ?? (mode === "edit" ? "Modifier un Magasin" : "Ajouter un Magasin");
  const submitLabel = submitLabelOverride ?? (mode === "edit" ? "Mettre à jour" : "Enregistrer");

  const normalize = (v: string) => v.trim().toUpperCase();

  const addCode = (raw: string) => {
    const parts = raw.split(/[,;\s\n]+/).map(normalize).filter(Boolean);
    if (parts.length === 0) return;
    setCodes((prev) => {
      const set = new Set(prev.map(normalize));
      parts.forEach((p) => set.add(p));
      return Array.from(set);
    });
    setCodeInput("");
  };

  const removeCode = (toRemove: string) => {
    setCodes((prev) => prev.filter((c) => normalize(c) !== normalize(toRemove)));
  };

  const onSubmit = async () => {
    setError(null);
    if (!name.trim()) return setError("Veuillez saisir un nom.");
    if (codes.length === 0) return setError("Veuillez saisir au moins un code.");
    setSaving(true);
    try {
      await onSave({ name: name.trim(), codes, selectedEntityTypes });
    } catch (e: any) {
      let msg = "Échec de l'enregistrement. Veuillez réessayer.";
      if (e?.status === 400) {
        try {
          const json = JSON.parse(e.message);
          if (json?.message) msg = json.message;
        } catch {}
      } else if (e?.status === 409) {
        try {
          const json = JSON.parse(e.message);
          if (json?.message) msg = json.message;
        } catch {}
      } else if (e?.status === 404) {
        msg = "Le magasin demandé est introuvable.";
      } else if (e?.status === 401) {
        msg = "Échec de l'enregistrement. Nous vous invitons à rafraîchir la page puis réessayer.";
      }
      setError(msg);
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  return (
    <div className="add-company-modal-backdrop">
      <div className="add-company-modal">
        <h3>{title}</h3>
        {error && <div className="form-error">{error}</div>}

        <div className="row">
          <label>Nom</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            placeholder="Ex: Contoso"
          />
        </div>

        <div className="row">
          <label>Codes</label>
          <div className="codes-input-group">
            <input
              className="codes-input"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Tab") {
                  e.preventDefault();
                  addCode(codeInput);
                }
              }}
              disabled={saving}
              placeholder="Ex: 1Q98 ou 55D11, 98T14, 65Y8"
            />
            <button
              type="button"
              className="codes-add-btn"
              onClick={() => addCode(codeInput)}
              disabled={saving || !codeInput.trim()}
            >
              Ajouter le code
            </button>
          </div>

          {codes.length > 0 && (
            <div className="code-chips">
              {codes.map((c) => (
                <span key={c} className="code-chip">
                  {c}
                  <button
                    type="button"
                    aria-label={`Supprimer ${c}`}
                    className="code-chip__remove"
                    onClick={() => removeCode(c)}
                    disabled={saving}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <small className="codes-hint">
            Astuce : utilisez <kbd>Entrée</kbd> ou <kbd>Tab</kbd> pour ajouter. Les doublons sont ignorés.
          </small>
        </div>

        <div className="row">
          <label>Entités</label>
          <div className="checkbox-group">
            {ENTITY_OPTIONS.map((st: EntityTypes, index: number) => (
              <label key={index} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedEntityTypes.includes(st)}
                  onChange={() => {
                    setSelectedEntityTypes((prev) =>
                      prev.includes(st) ? prev.filter((t) => t !== st) : [...prev, st]
                    );
                  }}
                  disabled={saving}
                />
                {EntityTypes[st]}
              </label>
            ))}
          </div>
        </div>

        <div className="add-company-modal-actions">
          <button onClick={onSubmit} disabled={saving}>
            {saving ? "Enregistrement..." : submitLabel}
          </button>
          <button onClick={onClose} disabled={saving}>
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCompanyModal;
